import { useTranslation } from '@/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AnchorItem,
  DEFAULT_ANCHOR,
  getActiveHref,
  getAnchorHrefs,
  getAnchorItems,
  getScrollTopForSection,
  MANUAL_SCROLL_THRESHOLD,
  RESET_SETTLE_MS,
  SCROLL_IDLE_THRESHOLD_MS,
  SCROLL_POSITION_TOLERANCE,
} from './anchorData';

type SharedRefs = {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  sectionRootRef: React.RefObject<HTMLDivElement>;
  /** True while a click-initiated smooth scroll owns the highlight. */
  isClickingRef: React.MutableRefObject<boolean>;
  /** Where that smooth scroll is heading, or null when none is running. */
  targetScrollRef: React.MutableRefObject<number | null>;
  /** Last anchor written, shared so click and scroll detection stay in sync. */
  lastAnchorRef: React.MutableRefObject<string | null>;
  setCurrentAnchor: (anchor: string) => void;
};

/**
 * Click handling: smooth-scroll to the section and hold the clicked item
 * highlighted until the animation settles, so the highlight does not flicker
 * through every section it passes on the way.
 */
const useAnchorClick = ({
  scrollContainerRef,
  sectionRootRef,
  isClickingRef,
  targetScrollRef,
  lastAnchorRef,
  setCurrentAnchor,
}: SharedRefs) => {
  const scrollEndCleanupRef = useRef<(() => void) | null>(null);
  // Invalidates callbacks still pending from an earlier click.
  const clickIdRef = useRef(0);

  const cleanupScrollDetection = useCallback(() => {
    if (scrollEndCleanupRef.current) {
      scrollEndCleanupRef.current();
      scrollEndCleanupRef.current = null;
    }
  }, []);

  const setupScrollEndDetection = useCallback(
    (
      scrollContainer: HTMLElement,
      targetScrollTop: number,
      clickId: number
    ) => {
      let idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
      let rafId: number | null = null;
      let lastScrollTop = scrollContainer.scrollTop;
      let isCleanedUp = false;

      const finishScrolling = () => {
        if (isCleanedUp) return;
        isCleanedUp = true;

        scrollContainer.removeEventListener('scrollend', handleScrollEnd);
        if (idleTimeoutId) clearTimeout(idleTimeoutId);
        if (rafId) cancelAnimationFrame(rafId);

        const releaseClickFlag = () => {
          if (clickIdRef.current !== clickId) return;
          isClickingRef.current = false;
          targetScrollRef.current = null;
        };

        // Safari queues scroll events that fire *after* `scrollend`; releasing
        // the clicking flag two frames later keeps them from stealing the
        // highlight back. Same guard as the payee drawer.
        requestAnimationFrame(() => requestAnimationFrame(releaseClickFlag));
      };

      const handleScrollEnd = () => finishScrolling();

      // Fallback for browsers without `scrollend`: treat "stopped moving" as done.
      const checkScrollIdle = () => {
        if (isCleanedUp) return;

        const currentScrollTop = scrollContainer.scrollTop;
        const reachedTarget =
          Math.abs(currentScrollTop - targetScrollTop) <=
          SCROLL_POSITION_TOLERANCE;
        const hasStoppedMoving = currentScrollTop === lastScrollTop;

        if (!reachedTarget && !hasStoppedMoving) {
          lastScrollTop = currentScrollTop;
          rafId = requestAnimationFrame(checkScrollIdle);
          return;
        }

        if (idleTimeoutId) clearTimeout(idleTimeoutId);
        idleTimeoutId = setTimeout(() => {
          if (scrollContainer.scrollTop === currentScrollTop) {
            finishScrolling();
          } else {
            lastScrollTop = scrollContainer.scrollTop;
            rafId = requestAnimationFrame(checkScrollIdle);
          }
        }, SCROLL_IDLE_THRESHOLD_MS);
      };

      scrollContainer.addEventListener('scrollend', handleScrollEnd, {
        once: true,
      });
      rafId = requestAnimationFrame(checkScrollIdle);

      return () => finishScrolling();
    },
    [isClickingRef, targetScrollRef]
  );

  const scrollToHref = useCallback(
    (href: string) => {
      const scrollContainer = scrollContainerRef.current;
      const section = sectionRootRef.current?.querySelector(href);
      if (!scrollContainer || !(section instanceof HTMLElement)) {
        return;
      }

      cleanupScrollDetection();

      clickIdRef.current += 1;
      const thisClickId = clickIdRef.current;

      isClickingRef.current = true;
      setCurrentAnchor(href);
      // Keep the scroll-detection cache in sync so it does not immediately
      // "correct" the anchor we just set.
      lastAnchorRef.current = href;

      const finalScrollTop = getScrollTopForSection(scrollContainer, section);
      targetScrollRef.current = finalScrollTop;

      scrollEndCleanupRef.current = setupScrollEndDetection(
        scrollContainer,
        finalScrollTop,
        thisClickId
      );

      scrollContainer.scrollTo({ top: finalScrollTop, behavior: 'smooth' });
    },
    [
      cleanupScrollDetection,
      isClickingRef,
      lastAnchorRef,
      scrollContainerRef,
      sectionRootRef,
      setCurrentAnchor,
      setupScrollEndDetection,
      targetScrollRef,
    ]
  );

  const handleAnchorClick = useCallback(
    (
      e: React.MouseEvent<HTMLElement>,
      link: { title: React.ReactNode; href: string }
    ) => {
      // Without this the browser jumps to the id and adds it to the URL.
      e.preventDefault();
      scrollToHref(link.href);
    },
    [scrollToHref]
  );

  return { handleAnchorClick, scrollToHref, cleanupScrollDetection };
};

/**
 * Keeps the highlighted anchor in sync with manual scrolling, and hands control
 * back to scroll detection the moment the user scrolls against an in-flight
 * click animation.
 */
const useScrollDetection = ({
  scrollContainerRef,
  sectionRootRef,
  isClickingRef,
  isResettingRef,
  targetScrollRef,
  lastAnchorRef,
  setCurrentAnchor,
  anchorItems,
  enabled,
}: SharedRefs & {
  isResettingRef: React.MutableRefObject<boolean>;
  anchorItems: AnchorItem[];
  enabled: boolean;
}) => {
  const rafIdRef = useRef<number | null>(null);
  const lastScrollTopRef = useRef<number | null>(null);

  // Read through a ref so the scroll listener never closes over stale items.
  const anchorItemsRef = useRef(anchorItems);
  anchorItemsRef.current = anchorItems;

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!enabled || !scrollContainer) {
      return;
    }

    /**
     * True when the user scrolls opposite to where a clicked smooth scroll is
     * heading — i.e. they took over and the click should stop owning the
     * highlight.
     */
    const isManualScrollDetected = (currentScrollTop: number): boolean => {
      const targetScroll = targetScrollRef.current;
      const lastScrollTop = lastScrollTopRef.current;
      if (!isClickingRef.current || targetScroll === null) return false;
      if (lastScrollTop === null) return false;

      const scrollDelta = currentScrollTop - lastScrollTop;
      const isOppositeDirection =
        scrollDelta > 0 !== targetScroll > lastScrollTop;

      return (
        isOppositeDirection && Math.abs(scrollDelta) > MANUAL_SCROLL_THRESHOLD
      );
    };

    const processScroll = () => {
      rafIdRef.current = null;

      const sectionRoot = sectionRootRef.current;
      if (isResettingRef.current || !sectionRoot) {
        return;
      }

      const currentScrollTop = scrollContainer.scrollTop;
      if (isManualScrollDetected(currentScrollTop)) {
        isClickingRef.current = false;
        targetScrollRef.current = null;
      }
      lastScrollTopRef.current = currentScrollTop;

      // A click owns the highlight until its animation settles.
      if (isClickingRef.current) {
        return;
      }

      const activeHref = getActiveHref(
        scrollContainer,
        sectionRoot,
        getAnchorHrefs(anchorItemsRef.current)
      );
      if (!activeHref || lastAnchorRef.current === activeHref) {
        return;
      }

      lastAnchorRef.current = activeHref;
      setCurrentAnchor(activeHref);
    };

    // Throttle to one update per frame — the drawer can render thousands of rows.
    const handleScroll = () => {
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(processScroll);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    // "Show remaining" (Transactions/Journal) grows the sections' height
    // without firing a scroll event, so the highlighted anchor would
    // otherwise stay stuck on whatever section used to be at the top.
    // Re-derive it whenever the sections' total height changes.
    const sectionRoot = sectionRootRef.current;
    let resizeObserver: ResizeObserver | null = null;
    if (sectionRoot && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(sectionRoot);
    }

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [
    enabled,
    isClickingRef,
    isResettingRef,
    lastAnchorRef,
    scrollContainerRef,
    sectionRootRef,
    setCurrentAnchor,
    targetScrollRef,
  ]);
};

type UseHandleAnchorParams = {
  /** The element that actually scrolls (the left pane's scroll area). */
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  /** Wrapper holding the anchored sections; anchor hrefs resolve inside it. */
  sectionRootRef: React.RefObject<HTMLDivElement>;
  /** Report currently shown — a change resets the anchor (NA-5579 AC1). */
  reportId: string;
  enabled: boolean;
};

/**
 * Anchor navigation for the Approval drawer (NA-5579), mirroring the payee
 * drawer's behaviour (NA-3475 / NA-3704): click to smooth-scroll, highlight
 * follows manual scrolling, and no artificial bottom padding — so navigating to
 * the last section leaves no blank space under it.
 */
export const useHandleAnchor = ({
  scrollContainerRef,
  sectionRootRef,
  reportId,
  enabled,
}: UseHandleAnchorParams) => {
  const { t } = useTranslation();
  const [currentAnchor, setCurrentAnchor] = useState<string>(DEFAULT_ANCHOR);

  const isClickingRef = useRef(false);
  const isResettingRef = useRef(false);
  const targetScrollRef = useRef<number | null>(null);
  const lastAnchorRef = useRef<string | null>(null);

  const anchorItems = useMemo(() => getAnchorItems(t), [t]);

  const sharedRefs: SharedRefs = {
    scrollContainerRef,
    sectionRootRef,
    isClickingRef,
    targetScrollRef,
    lastAnchorRef,
    setCurrentAnchor,
  };

  const { handleAnchorClick, cleanupScrollDetection } =
    useAnchorClick(sharedRefs);

  useScrollDetection({
    ...sharedRefs,
    isResettingRef,
    anchorItems,
    enabled,
  });

  const resetSettleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const resetAnchor = useCallback(() => {
    cleanupScrollDetection();
    isClickingRef.current = false;
    targetScrollRef.current = null;
    lastAnchorRef.current = DEFAULT_ANCHOR;
    setCurrentAnchor(DEFAULT_ANCHOR);

    // Jump rather than animate: this runs when the drawer switches reports, and
    // an animation there reads as the previous report scrolling away.
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' });

    // Mute scroll detection until the jump has settled, otherwise the scroll
    // event it fires would immediately re-derive an anchor from the old layout.
    isResettingRef.current = true;
    if (resetSettleTimeoutRef.current) {
      clearTimeout(resetSettleTimeoutRef.current);
    }
    resetSettleTimeoutRef.current = setTimeout(() => {
      isResettingRef.current = false;
    }, RESET_SETTLE_MS);
  }, [cleanupScrollDetection, scrollContainerRef]);

  // AC1: opening another payment request resets the anchor to the first section.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    resetAnchor();
  }, [enabled, reportId, resetAnchor]);

  useEffect(
    () => () => {
      if (resetSettleTimeoutRef.current) {
        clearTimeout(resetSettleTimeoutRef.current);
      }
    },
    []
  );

  // antd's `Anchor.getContainer` is typed as `() => Window | HTMLElement`.
  // `globalThis` (Sonar S7764) doesn't structurally satisfy that `Window` type
  // here — this app's global.d.ts augments `Window` with APP_ENV/FEATURE_FLAG/
  // documentPictureInPicture, which `typeof globalThis` doesn't pick up — so
  // swapping in `globalThis` fails `tsc`. Keeping `window` intentionally.
  const getContainer = useCallback(
    () => scrollContainerRef.current || window,
    [scrollContainerRef]
  );

  const getCurrentAnchor = useCallback(() => currentAnchor, [currentAnchor]);

  return {
    anchorItems,
    getContainer,
    getCurrentAnchor,
    handleAnchorClick,
  };
};
